/**
 * Tile atlas — loads terrain tile PNGs at bootstrap and produces
 * CanvasPatterns for the bake pass.
 *
 * The rendering strategy shifted at v0M2.6e from solid-fill palette
 * per cell to sampling from AI-generated terrain textures (per §6.1
 * #12 art pipeline). Each tile is a seamless-tileable painted texture
 * covering one Terrain kind; the bake pass uses `ctx.createPattern`
 * with the tile as source, then fills each cell's rect at
 * `(x * z, y * z)` — the canvas API's pattern-fill anchoring means
 * the pixel actually drawn is the tile's pixel at
 * `(x * z % tileW, y * z % tileH)`, giving the "tile repeats across
 * the map" effect for free.
 *
 * **v0M12.1 (2026-07-26) — variants.** Each terrain kind may have
 * multiple tile files: `<slot>.png` (the base) + optional
 * `<slot>-1.png`, `<slot>-2.png`, ... — the atlas loads whichever are
 * present. The renderer picks a variant deterministically per cell
 * (hash of cellIndex → variant index) so the same map always looks
 * the same. Skips missing variant files without warning; the base is
 * required per-terrain, all suffix variants are optional.
 *
 * **v0M12.1** — River (Terrain.River = 9) added to the slot list.
 * Missing river.png loads the atlas without it; renderer falls back
 * to the palette blue via `terrainColor(9)`. Ships whenever Michael
 * drops river.png into public/assets/terrain/.
 *
 * Fallback: if the BASE tile for any terrain is missing, atlas load
 * fails softly (returns null) and the renderer takes the v0M2.6d
 * solid-fill + noise + hillshading path. No hard crash.
 */

import { Terrain } from './terrain-palette';

/**
 * Base filenames — kebab-case slugs matching Terrain enum values and
 * `scripts/gen-terrain-tiles.mjs` output slots.
 */
const TILE_SLOTS: Readonly<Record<number, string>> = {
  [Terrain.Plains]: 'plains',
  [Terrain.Farmland]: 'farmland',
  [Terrain.Forest]: 'forest',
  [Terrain.Hills]: 'hills',
  [Terrain.Mountains]: 'mountains',
  [Terrain.Desert]: 'desert',
  [Terrain.Swamp]: 'swamp',
  [Terrain.Lake]: 'lake',
  [Terrain.Sea]: 'sea',
  // v0M12.1 (2026-07-26) — River tile slot. Missing file → atlas
  // loads without the river tile; renderer falls back to palette
  // colour (which will show through the river-render overlay).
  [Terrain.River]: 'river',
};

/**
 * Maximum variant index to probe per terrain slot. Attempts to load
 * `<slot>.png`, `<slot>-1.png`, ..., `<slot>-MAX_VARIANT_INDEX.png`.
 * Missing indices in the middle of the range are skipped without
 * halting the probe — this lets Michael add tiles at any suffix and
 * lets existing gaps (e.g. `farmland-1.png` / `-2.png` absent while
 * `-3.png`+ present) still load the tiles that ARE on disk.
 *
 * Set to 0 at 2026-07-29 (session 35 v0M12+art) to restore pre-
 * v0M12.1 "one big painted region" look. Rationale: with multiple
 * variants active, the per-cell deterministic picker shreds each
 * biome into a checkerboard where tonally-different variants land
 * next to each other. Loading only base tiles means every plains
 * cell fills with the same `plains.png` pattern (anchored to canvas
 * coord 0,0 by `createPattern`), so the tile art repeats seamlessly
 * across the whole plains region at its natural 8-32 cell scale.
 * Variants on disk are preserved for post-v1 curation; bump this
 * back once tonally-normalised or manually culled.
 */
const MAX_VARIANT_INDEX = 0;

/**
 * Target size (px) tiles are downsampled to at load time. The
 * Stability-generated tiles are 1024×1024 but painted as scenes with
 * large hero features — using them at native size means one tile-copy
 * covers a huge fraction of the map, and the painted feature shows
 * through at map scale (the "giant plant" problem). Downsampling to
 * TILE_TARGET_SIZE means each tile-copy covers TILE_TARGET_SIZE / z
 * cells; at 4 px/cell with 256px tiles, that's 64 cells per copy.
 * Multiple copies fit across the map and the tile reads as texture
 * rather than a painted scene.
 */
export const TILE_TARGET_SIZE = 128;

export interface TileAtlas {
  /** Downsampled tile canvases per terrain id. Index 0 = base tile,
   *  1..N = variants. Always has at least the base at index 0. */
  readonly tiles: Readonly<Record<number, HTMLCanvasElement[]>>;
  /**
   * CanvasPattern per terrain id + variant index. Patterns are
   * context-scoped so they can't be shared across offscreen canvases
   * without regeneration.
   */
  patternsFor(ctx: CanvasRenderingContext2D): Readonly<Record<number, CanvasPattern[]>>;
  /**
   * v0M12.1 (2026-07-26) — Deterministic variant picker. Given a
   * cellIndex (`y * width + x`), returns the variant index in
   * [0, tiles[terrainId].length). Uses a cheap integer hash so the
   * same cell always maps to the same variant across renders +
   * across sessions.
   */
  pickVariant(terrainId: number, cellIndex: number): number;
}

/**
 * Kick off image loads in parallel. Resolves with an atlas once every
 * BASE tile loads (variants are best-effort — missing ones drop
 * silently). Rejects if any BASE tile fails so the renderer takes
 * the fallback path deliberately rather than half-tiled.
 *
 * Exception: River (id 9) is a soft-required tile at v0M12.1 — if
 * `river.png` is missing, the atlas still loads without it and the
 * renderer falls back to palette blue for river cells. This lets the
 * mechanic ship before Michael sources the river tile.
 */
export async function loadTileAtlas(): Promise<TileAtlas | null> {
  const ids = Object.keys(TILE_SLOTS).map((s) => Number(s));

  // Build per-terrain variant lists. Base + probed variants; missing
  // variants trim the list (no error).
  const perTerrain: Record<number, HTMLCanvasElement[]> = {};
  const softMissing: number[] = []; // Track terrains missing their base.
  for (const id of ids) {
    const slot = TILE_SLOTS[id]!;
    const variantImgs: HTMLImageElement[] = [];
    // Base — required (except River which is soft-optional).
    try {
      variantImgs.push(await loadImage(`/assets/terrain/${slot}.png`));
    } catch {
      if (id === Terrain.River) {
        softMissing.push(id);
        continue; // Don't crash the whole atlas over a missing river tile.
      }
      // Base of a hard-required terrain missing — kick the atlas back.
      // eslint-disable-next-line no-console
      console.warn(`[SU] tile atlas load failed: missing base tile ${slot}.png`);
      return null;
    }
    // Suffix variants — best-effort. Probe 1..MAX_VARIANT_INDEX,
    // collecting every hit; skip gaps without halting. Session 35
    // v0M12+art amendment (was: stop-on-first-miss). Reason: gaps in
    // the suffix sequence are load-bearing — pre-fix, `farmland-1.png`
    // absent hid `-3/-4/-5` entirely, and every biome silently capped
    // at whatever contiguous prefix existed.
    for (let v = 1; v <= MAX_VARIANT_INDEX; v++) {
      try {
        variantImgs.push(await loadImage(`/assets/terrain/${slot}-${v}.png`));
      } catch {
        // Skip this suffix; continue probing higher indices.
      }
    }
    // Session 35 v0M12+art revert (2026-07-29): MFix10.5 tile-rotation
    // pseudo-variants removed. In practice more/better source tiles didn't
    // read as improved variety on the map, so paying for 4× canvas ops
    // per tile (+ 4× pattern-slot count) isn't earning its keep. Each
    // authored variant maps 1:1 to a pattern slot again.
    perTerrain[id] = variantImgs.map((img) => downsampleToCanvas(img, TILE_TARGET_SIZE));
  }
  const variantsPresent = Object.values(perTerrain).reduce(
    (acc, arr) => acc + arr.length,
    0,
  );
  // eslint-disable-next-line no-console
  console.info(
    `[SU] tile atlas loaded: ${Object.keys(perTerrain).length} terrains, ` +
      `${variantsPresent} tile files total (${MAX_VARIANT_INDEX}+ variants max/kind); ` +
      `soft-missing: ${softMissing.length === 0 ? 'none' : softMissing.map((id) => TILE_SLOTS[id]).join(', ')}`,
  );
  return {
    tiles: perTerrain,
    patternsFor(ctx: CanvasRenderingContext2D): Readonly<Record<number, CanvasPattern[]>> {
      const patterns: Record<number, CanvasPattern[]> = {};
      for (const id of ids) {
        const canvases = perTerrain[id];
        if (!canvases) { patterns[id] = []; continue; }
        patterns[id] = canvases.map((c) => ctx.createPattern(c, 'repeat')!).filter((p) => p !== null);
      }
      return patterns;
    },
    pickVariant(terrainId: number, cellIndex: number): number {
      const arr = perTerrain[terrainId];
      if (!arr || arr.length <= 1) return 0;
      // Cheap 32-bit integer hash mix. Deterministic per (terrainId, cellIndex).
      let h = (cellIndex * 2654435761) >>> 0;
      h ^= (terrainId * 40503) >>> 0;
      h = (h ^ (h >>> 16)) >>> 0;
      return h % arr.length;
    },
  };
}

/**
 * Draw the source image into an offscreen canvas at (size × size).
 * The blit uses browser default smoothing which softens the downsample
 * — usually the right call for painted textures at this scale.
 */
function downsampleToCanvas(src: HTMLImageElement, size: number): HTMLCanvasElement {
  const off = document.createElement('canvas');
  off.width = size;
  off.height = size;
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('downsampleToCanvas: 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, size, size);
  return off;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}
